/**
 * A site visit in Next up and Upcoming — `docs/new-request.md` §15.1, Board
 * 10 A, Board 9 E and F.
 *
 * Three rows, all of them about the one thing a request is waiting on:
 *
 * - **booked** — a date tile, *Site visit · 10:00–11:30 AM*, *Tom Reyes · at
 *   the property · Renee confirmed by text 9:52 AM*, a **Confirmed** tag once
 *   the customer replied, and what they asked for under it;
 * - **not booked yet** — dashed and green, *Site visit · not booked yet*, *Tom
 *   Reyes · 1 h 30 min · wants it by Wed, Nov 25*, the first line of what they
 *   want, and **Book a time**;
 * - **no site visit yet** — a job New request made without one: dashed and
 *   grey, with **Book a site visit**.
 *
 * Both buttons open New request in book mode (§11.4) on this job.
 */
import { clock, clockRange, minutesWords } from '@rowted/shared'
import { Button } from '../components/primitives.js'
import { cn } from '../lib/cn.js'
import { IconCalendar } from '../frame/icons.js'
import { openNewRequest } from '../requests/open-request.js'
import type { RecordActivity } from '../lib/use-record.js'

/** Minutes after midnight, and the weekday and day, where this browser is. */
function localOf(iso: string): { minutes: number; weekday: string; day: number } {
  const at = new Date(iso)
  return {
    minutes: at.getHours() * 60 + at.getMinutes(),
    weekday: at.toLocaleDateString('en-US', { weekday: 'short' }),
    day: at.getDate(),
  }
}

/** The words out of a note's markup — a tag is never a word. */
const wordsOf = (html: string): string =>
  html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/[ \t]+/g, ' ').trim()

/**
 * The first line of what they want, for a row that has room for one — its
 * first sentence, and *…* when there is more (Board 9 F: *…with a gas fire
 * pit…*).
 */
export const firstLine = (html: string): string => {
  const all = wordsOf(html)
  const line = all.split(/\n/)[0]!.trim()
  const sentence = /^.+?[.!?](?=\s|$)/.exec(line)?.[0] ?? line
  const cut = sentence.length > 90 ? sentence.slice(0, 90).replace(/\s+\S*$/, '') : sentence
  return cut.length < all.length ? `${cut.replace(/[.!?,;:]$/, '')}…` : cut
}

/** *Renee confirmed by text 9:52 AM* · *Renee was texted* · nothing. */
function confirmationWords(item: RecordActivity): string | null {
  const facts = item.appointment
  if (!facts) return null
  const who = facts.contactFirstName ?? 'The customer'
  if (facts.confirmedAt) {
    const by = facts.confirmedVia === 'email' ? 'email' : facts.confirmedVia === 'phone' ? 'phone' : 'text'
    return `${who} confirmed by ${by} ${clock(localOf(facts.confirmedAt).minutes)}`
  }
  if (facts.sent === 'sms') return `${who} was texted`
  if (facts.sent === 'email') return `${who} was emailed`
  return null
}

/** Boards 9 E and F draw both Book buttons at 12.5px with 14px each side (the kit's is 12 and 10). */
const BOOK_BUTTON = 'shrink-0 !px-3.5 !text-[12.5px]'

/** Board 10 A's tag. */
function ConfirmedTag() {
  return (
    <span className="inline-flex h-5 shrink-0 items-center whitespace-nowrap rounded-tag bg-accent-soft px-1.5 text-[11px] font-semibold text-accent-dark">
      Confirmed
    </span>
  )
}

export function AppointmentRow({ item }: { item: RecordActivity }) {
  const facts = item.appointment!
  const minutes = item.durationMinutes ?? null

  if (!item.dueAt) {
    const sub = [item.assigneeName, minutes ? minutesWords(minutes) : null,
      facts.wanted ? `wants it ${facts.wanted}` : null].filter(Boolean).join(' · ')
    return (
      <div className="border-t border-line-soft px-[18px] py-3" data-appointment="not-booked">
        <div className="flex items-center gap-3 rounded-[12px] border-[1.5px] border-dashed border-accent-line bg-green-band-bg p-3">
          <span className="inline-flex h-12 w-11 shrink-0 items-center justify-center rounded-[10px] border-[1.5px] border-dashed border-accent-line bg-surface text-accent">
            <IconCalendar size={18} />
          </span>
          <div className="flex min-w-0 flex-col">
            <b className="truncate text-[13.5px] font-bold text-text">Site visit · not booked yet</b>
            {sub && <span className="truncate text-[12px] text-text-muted">{sub}</span>}
          </div>
          <span className="flex-1" />
          {facts.jobId && (
            <Button variant="primary" className={BOOK_BUTTON}
              onClick={() => openNewRequest({ book: { jobId: facts.jobId!, appointmentId: item.id } })}>
              <IconCalendar size={14.5} />
              Book a time
            </Button>
          )}
        </div>
        {item.body && (
          <div className="mt-2 text-[11.5px] leading-[1.45] text-text-muted">{firstLine(item.body)}</div>
        )}
      </div>
    )
  }

  const at = localOf(item.dueAt)
  const confirmation = confirmationWords(item)
  const sub = [item.assigneeName, 'at the property', confirmation].filter(Boolean).join(' · ')
  const facts2 = [facts.leadSource ? `Lead source ${facts.leadSource}` : null,
    facts.wanted ? `wants it ${facts.wanted}` : null,
    facts.budget ? `budget ${facts.budget}` : null].filter(Boolean)

  return (
    <div className="border-t border-line-soft px-[18px] py-3" data-appointment="booked">
      <div className="flex items-center gap-3 rounded-[12px] border border-line p-3">
        <span className="inline-flex h-12 w-11 shrink-0 flex-col items-center justify-center rounded-[10px] border-[1.5px] border-accent-line bg-accent-soft text-accent-dark">
          <span className="text-[10px] font-bold uppercase leading-none">{at.weekday}</span>
          <b className="text-[17px] font-bold leading-none">{at.day}</b>
        </span>
        <div className="flex min-w-0 flex-col">
          <b className="truncate text-[13.5px] font-bold text-text">
            Site visit · {minutes ? clockRange(at.minutes, at.minutes + minutes) : clock(at.minutes)}
          </b>
          <span className="truncate text-[12px] text-text-muted">{sub}</span>
        </div>
        <span className="flex-1" />
        {facts.confirmedAt && <ConfirmedTag />}
      </div>
      {/*
        * What they asked for: the description when there is one, and the lead
        * source, the wish and the budget whenever any of them is known — a
        * request with no words in it still said when and how much.
        */}
      {facts.fromRequest && (item.body || facts2.length > 0) && (
        <>
          <div className="mb-1 mt-3 text-[11.5px] font-semibold text-text-muted">What they asked for</div>
          {item.body && (
            <div className="whitespace-pre-line text-[12.5px] leading-[1.5] text-text-body">{wordsOf(item.body)}</div>
          )}
          {facts2.length > 0 && (
            <div className={cn('text-[11.5px] text-text-faint', item.body && 'mt-2')}>{facts2.join(' · ')}</div>
          )}
        </>
      )}
    </div>
  )
}

/** Board 9 E: a job New request made with no site visit. */
export function NoSiteVisitRow({ jobId }: { jobId: string }) {
  return (
    <div className="px-[18px] py-3" data-appointment="none">
      <div className="flex items-center gap-3 rounded-[12px] border-[1.5px] border-dashed border-line-strong p-3">
        <span className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] border border-line bg-line-soft text-text-muted">
          <IconCalendar size={17} />
        </span>
        <div className="flex min-w-0 flex-col">
          <b className="truncate text-[13.5px] font-bold text-text">No site visit yet</b>
          <span className="truncate text-[12px] text-text-muted">the request was created without an appointment</span>
        </div>
        <span className="flex-1" />
        <Button className={BOOK_BUTTON} onClick={() => openNewRequest({ book: { jobId } })}>
          <IconCalendar size={14.5} />
          Book a site visit
        </Button>
      </div>
    </div>
  )
}
